import { useCallback, useEffect, useRef, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { PlusIcon } from "lucide-react";
import uuid from "uuid";

import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card } from "simplydo/react/ui/card";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H1, H2, JustifiedHeader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
} from "simplydo/react/ui/placeholder";

import api from "api";
import DiscoverImage from "src/images/discover.png";
import { useAppSelector } from "store";
import util from "utils/utils";

import NewChallengeModal from "components/challenges/NewChallengeModal";
import ChallengeCard from "components/lib/Cards/ChallengeCard";
import { OrgSwitcherDropdown } from "components/lib/OrgSwitcherTray";
import { EmptyBox } from "components/lib/UI";

const Challenges = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const [isLoading, setIsLoading] = useState(true);
  const currentFetchId = useRef("");
  const [newChallengeOpen, setNewChallengeOpen] = useState(false);
  const [challengeData, setChallengeData] = useState<
    OpenAPI.GET<"/challenges/all">["response"] | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();

  const { challenges } = challengeData ?? {};

  const fetchChallenges = useThrottle(
    (query) => {
      const fetchId = uuid.v4();
      currentFetchId.current = fetchId;

      api.challenges.getAll(
        { query },
        (data) => {
          if (fetchId !== currentFetchId.current) return;
          setChallengeData(data);
          setIsLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setIsLoading(false);
        },
      );
    },
    400,
    [],
  );

  const getChallenges = useCallback(
    (query: string) => {
      setIsLoading(true);
      setSearchQuery(query);
      fetchChallenges(query);
    },
    [fetchChallenges],
  );

  useEffect(() => {
    getChallenges("");
  }, [getChallenges, user?.ownerOrganisation?._id]);

  return (
    <div>
      {newChallengeOpen ? (
        <NewChallengeModal
          newChallengeOpen={newChallengeOpen}
          onCloseModal={() => setNewChallengeOpen(false)}
        />
      ) : null}
      <Helmet title={t("common:capitalise", { key: "generic.challenges" })} />
      <div style={{ marginTop: 20 }}>
        <Container>
          <JustifiedHeader>
            <div>
              <H1 style={{ color: "#3b3b3b", margin: "0px" }}>
                {user
                  ? t("common:capitalise", { key: "generic.challenges" })
                  : t("challenges.yours.sidebar.all")}
              </H1>
              {user && (user.ownerOrganisations?.length ?? 0) > 1 ? (
                <div
                  style={{
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "baseline",
                    gap: "0.25em",
                  }}
                >
                  <span>
                    in{" "}
                    <span style={{ fontWeight: 600 }}>
                      {user.ownerOrganisation?.name}
                    </span>
                    {" -"}
                  </span>
                  <OrgSwitcherDropdown
                    basic
                    trigger={
                      <a
                        tabIndex={0}
                        role="button"
                        onClick={(e) => e.preventDefault()}
                      >
                        Change organisation
                      </a>
                    }
                  />
                </div>
              ) : null}
            </div>
            {util.canCreateChallenges(user) && (
              <Button
                size={!theme.sizes.isComputer ? "small" : "medium"}
                onClick={() => setNewChallengeOpen(true)}
              >
                <Icon icon={PlusIcon} />
                {!theme.sizes.isComputer
                  ? `Create ${t("generic.challengeWithArticle")}`
                  : t("challenges.new.title")}
              </Button>
            )}
          </JustifiedHeader>
          <Input
            placeholder={t("challenges.all.search")}
            loading={isLoading}

            onChange={(_e, { value }) => getChallenges(value)}
            value={searchQuery}
            style={{ width: "33%" }}
          />
        </Container>
      </div>

      <Container style={{ marginTop: "1rem" }}>
        {!challenges?.length && !isLoading ? (
          searchQuery ? (
            <EmptyBox style={{ width: "100%" }}>
              <img
                src={DiscoverImage}
                style={{
                  maxHeight: 150,
                  maxWidth: "70%",
                  display: "block",
                  margin: "10px auto",
                }}
                alt="Discover"
              />
              <H2>{t("ideas.yours.sidebar.noResults")}</H2>
            </EmptyBox>
          ) : (
            <>
              <EmptyBox style={{ width: "100%" }}>
                <img
                  src={DiscoverImage}
                  style={{
                    maxHeight: 150,
                    maxWidth: "70%",
                    display: "block",
                    margin: "10px auto",
                  }}
                  alt="Discover"
                />

                <H2>{t("challenges.all.noneOpen")}</H2>
              </EmptyBox>
              <Divider hidden />
            </>
          )
        ) : (
          <>
            <div style={{ width: "100%" }}>
              <Grid columns={4} mobile={1} tablet={2} gap="1rem">
                {!challenges?.length && isLoading
                  ? Array.from({ length: 8 }).map((_, i) => (
                      <GridColumn key={i}>
                        <Card fluid>
                          <Placeholder>
                            <PlaceholderParagraph>
                              <PlaceholderLine style={{ height: 200 }} />
                              <PlaceholderLine style={{ height: 50 }} />
                            </PlaceholderParagraph>
                          </Placeholder>
                        </Card>
                      </GridColumn>
                    ))
                  : challenges?.map((challenge) => (
                      <GridColumn key={challenge._id}>
                        <ChallengeCard
                          showIdeas
                          challenge={challenge}
                          ideas={challenge.ideas}
                        />
                      </GridColumn>
                    ))}
              </Grid>
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default Challenges;
